/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificatesTab
 * ---------------
 * Participants → Certificates: one row per participant of an event, and the
 * Generate that issues their certificate. Existing endpoints only:
 *
 *   POST /arcee/application/table/fetch/          passes, registrations, activities,
 *                                                 choices, certificates, templates
 *   POST /arcee/application/table/read/records/   the pass again, just before Generate
 *   POST /arcee/application/table/save/records/   Generate: the pass's corrected
 *                                                 details + the certificate row, one batch
 *
 * Saving the certificate row fires the record.created hook, which emails the
 * participant (docs/certificates/certificate_email_hook.py). No PDF is made:
 * the participant's browser builds it on download.
 */

import envConfig from '../../../config/envConfig';
import {
  ACTIVITY_TABLE, CheckInRequestError, PASS_STATUSES, PASS_TABLE, REGISTRATION_TABLE, fetchRows, passFilters,
} from './checkInApi';
import { idOf, infoOf, nameOf } from './checkInRules';

export const TEMPLATE_TABLE = 'certificate_template';
export const CHOICE_TABLE = 'event_activity_certificate_template';
export const CERTIFICATE_TABLE = 'user_certificate_information';

export const CHECKED_IN = ['CHECKED_IN', 'CHECKED_OUT'];
export const NAME_MAX = 64;
export const EMAIL_MAX = 254;
export const MOBILE_DIGITS = 10;
export const POSITION_DIGITS = 6;

const equals = (column, value) => ({ column_key: column, filter_type: 'equals', column_value: value });
const byId = (rows) => new Map((rows || []).map((row) => [String(idOf(row.id)), row]));

/** Everything the tab shows for one event, in parallel reads. */
export async function fetchEventCertificateData(api, eventId) {
  const registrations = await fetchRows(api, REGISTRATION_TABLE, {
    filters: [equals('event_id', eventId), equals('status', 'CONFIRMED')], pageSize: 25000,
  });
  const registrationIds = registrations.map((row) => idOf(row.id)).filter((id) => id != null);
  const [passes, activities, choices, certificates, templates, routes, venues] = await Promise.all([
    registrationIds.length
      ? fetchRows(api, PASS_TABLE, { filters: passFilters([eventId], PASS_STATUSES.all, registrationIds), pageSize: 25000 })
      : Promise.resolve([]),
    fetchRows(api, ACTIVITY_TABLE, { filters: [equals('event_id', eventId)], pageSize: 500 }),
    fetchRows(api, CHOICE_TABLE, { filters: [equals('event_id', eventId)], pageSize: 500 }),
    fetchRows(api, CERTIFICATE_TABLE, { filters: [equals('event_id', eventId)], pageSize: 25000 }),
    // Every template, retired ones too: an activity may still point at one.
    fetchRows(api, TEMPLATE_TABLE, { pageSize: 500 }),
    // For templates with a map: the course, or the venue when there is none.
    // Optional: a failure only leaves the map without its line.
    fetchRows(api, 'event_activity_route', { pageSize: 500 }).catch(() => []),
    fetchRows(api, 'event_activity_venue', { pageSize: 500 }).catch(() => []),
  ]);
  return { registrations, passes, activities, choices, certificates, templates, routes, venues };
}

/**
 * The pass as it is now, read just before Generate — the list may be minutes
 * old, and a certificate must only go to somebody who really checked in.
 */
export async function fetchPassNow(api, passId) {
  let resp;
  try {
    resp = await api.apiCall('/arcee/application/table/read/records/', {
      method: 'POST',
      body: JSON.stringify({
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        operations: [{ ref: 'p', table: PASS_TABLE, where: { id: passId } }],
      }),
    });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) throw new CheckInRequestError('The pass could not be read.', { status: resp.status, body });
  const block = (body?.results || body)?.p;
  return block && block.status === 'ok' && Array.isArray(block.data) ? block.data[0] || null : null;
}

export async function saveBatch(api, operations) {
  let resp;
  try {
    resp = await api.apiCall('/arcee/application/table/save/records/', {
      method: 'POST',
      body: JSON.stringify({
        organization_id: Number(envConfig.organizationId),
        application_id: Number(envConfig.applicationId),
        operations,
      }),
    });
  } catch (error) {
    throw new CheckInRequestError(error?.message || 'No answer from the server.');
  }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) throw new CheckInRequestError(body?.error || 'The certificate was not generated.', { status: resp.status, body });
  return body;
}

/* ---------------------------------------------------------------- pure --- */

/**
 * { activityId: { routeLine, venueName } }: each activity's course
 * (route_polyline) and venue name, for templates that draw a map.
 */
export function coursesByActivity(routes, venues, activityIds) {
  const wanted = new Set((activityIds || []).map(String));
  const out = {};
  (venues || []).forEach((venue) => {
    const id = String(idOf(venue.event_activity_id));
    if (wanted.has(id)) out[id] = { ...(out[id] || {}), venueName: String(venue.venue_name || ''), venue: venue.venue_point || null };
  });
  (routes || []).forEach((route) => {
    const id = String(idOf(route.event_activity_id));
    if (wanted.has(id) && route.route_polyline) out[id] = { ...(out[id] || {}), routeLine: route.route_polyline };
  });
  return out;
}

/** Row states, in the order the filter chips show them. */
export const STATES = {
  ready: { label: 'Ready to generate', tone: 'ready' },
  issued: { label: 'Issued', tone: 'issued' },
  not_checked_in: { label: 'Not checked in', tone: 'none' },
  no_template: { label: 'No template', tone: 'warn' },
};


/**
 * One row per pass: who, which activity, its template choice, its certificate
 * if it has one, and its state.
 *   issued          a certificate exists
 *   not_checked_in  the pass is not CHECKED_IN / CHECKED_OUT
 *   no_template     the activity has no template chosen (event wizard, step 4)
 *   ready           can be generated
 */
export function buildParticipantRows({ registrations, passes, activities, choices, certificates, templates }) {
  const registrationById = byId(registrations);
  const activityById = byId(activities);
  const templateById = byId(templates);
  const choiceByActivity = new Map((choices || []).map((row) => [String(idOf(row.event_activity_id)), row]));
  const certificateByPass = new Map((certificates || []).map((row) => [String(idOf(row.pass_id)), row]));
  const onlyActivity = (activities || []).length === 1 ? activities[0] : null;

  return (passes || []).map((pass) => {
    const registration = registrationById.get(String(idOf(pass.registration_id)));
    const activityId = idOf(registration?.event_activity_id) ?? (onlyActivity ? idOf(onlyActivity.id) : null);
    const activity = activityId != null ? activityById.get(String(activityId)) : null;
    const choice = activityId != null ? choiceByActivity.get(String(activityId)) || null : null;
    const template = choice ? templateById.get(String(idOf(choice.certificate_template_id))) || null : null;
    const certificate = certificateByPass.get(String(pass.id)) || null;
    const info = infoOf(pass.user_info) || {};
    let state = 'ready';
    if (certificate) state = 'issued';
    else if (!CHECKED_IN.includes(pass.status)) state = 'not_checked_in';
    else if (!choice || !template) state = 'no_template';
    return {
      passId: pass.id,
      passcode: pass.passcode || '',
      status: pass.status,
      userId: pass.user_id ?? null,
      info,
      name: nameOf(pass.user_info),
      email: String(info.email || ''),
      activityId,
      activityName: activity ? String(activity.name || '') : '',
      choice,
      template: template ? { id: String(idOf(template.id)), name: template.name || '', url: template.template_url || '' } : null,
      certificate,
      state,
    };
  });
}

export function countStates(rows) {
  const counts = { all: rows.length, ready: 0, issued: 0, not_checked_in: 0, no_template: 0 };
  rows.forEach((row) => { counts[row.state] += 1; });
  return counts;
}

/** Rows for a state chip ('all' or a STATES key) and a search over name, email and passcode. */
export function filterRows(rows, state, search) {
  const term = String(search || '').trim().toLowerCase();
  return rows.filter((row) => (state === 'all' || row.state === state)
    && (!term || `${row.name} ${row.email} ${row.passcode} ${row.activityName}`.toLowerCase().includes(term)));
}

/** The edit form, from the pass's user_info. */
export function detailsForm(info = {}) {
  return {
    firstName: String(info.first_name || ''),
    lastName: String(info.last_name || ''),
    email: String(info.email || ''),
    countryCode: String(info.country_code || '+91'),
    mobileNumber: String(info.mobile_number || ''),
    finishTime: '',
    position: '',
  };
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const FINISH_TIME = /^\d{2}:[0-5]\d:[0-5]\d$/;

/*
 * Input filters: applied on every keystroke, so a field can only ever hold
 * what its column accepts. The checks below still run on save.
 */
export const onlyDigits = (value, max) => String(value || '').replace(/\D/g, '').slice(0, max);

/** "+" and up to three digits, e.g. +91. */
export function countryCodeInput(value) {
  const digits = onlyDigits(value, 3);
  return digits ? `+${digits}` : (String(value || '').startsWith('+') ? '+' : '');
}

/** Digits typed into a finish time become HH:MM:SS as they are typed: 014530 → 01:45:30. */
export function finishTimeInput(value) {
  const digits = onlyDigits(value, 6);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)].filter(Boolean).join(':');
}

/** { field: message } — empty when Generate may go ahead. */
export function validateDetails(form) {
  const errors = {};
  const first = form.firstName.trim();
  const last = form.lastName.trim();
  if (!first) errors.firstName = 'Enter the first name.';
  else if (first.length > NAME_MAX) errors.firstName = `At most ${NAME_MAX} characters.`;
  if (!last) errors.lastName = 'Enter the last name.';
  else if (last.length > NAME_MAX) errors.lastName = `At most ${NAME_MAX} characters.`;
  const email = form.email.trim();
  if (!email) errors.email = 'Enter the email address. The certificate link will be sent to this email.';
  else if (email.length > EMAIL_MAX) errors.email = `At most ${EMAIL_MAX} characters.`;
  else if (!EMAIL.test(email)) errors.email = 'Enter a valid email address, like name@example.com.';
  const code = form.countryCode.trim();
  const mobile = form.mobileNumber.trim();
  if (mobile && !new RegExp(`^\\d{${MOBILE_DIGITS}}$`).test(mobile)) errors.mobileNumber = `Enter all ${MOBILE_DIGITS} digits.`;
  if (mobile && !/^\+\d{1,3}$/.test(code)) errors.countryCode = 'Like +91.';
  const finish = form.finishTime.trim();
  if (finish && !FINISH_TIME.test(finish)) {
    errors.finishTime = 'Hours, minutes and seconds, like 01:45:30. Minutes and seconds go up to 59.';
  }
  const position = String(form.position).trim();
  if (position && (!new RegExp(`^\\d{1,${POSITION_DIGITS}}$`).test(position) || Number(position) < 1)) {
    errors.position = 'A whole number, 1 or more.';
  }
  return errors;
}

/** The pass's user_info with the corrected details; every other key is kept. */
export function mergeUserInfo(info, form) {
  const merged = {
    ...(info || {}),
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    email: form.email.trim(),
  };
  const mobile = form.mobileNumber.trim();
  if (mobile) {
    merged.mobile_number = mobile;
    merged.country_code = form.countryCode.trim();
  } else {
    delete merged.mobile_number;
  }
  return merged;
}

/**
 * Generate, as one all-or-nothing batch: the pass's details are corrected and
 * the certificate row is created together, or neither is. The server creates
 * certificate_no; the record.created hook sends the email.
 */
export function buildGenerateBatch(row, form, eventId) {
  const position = String(form.position).trim();
  return [
    { table: PASS_TABLE, data: { id: row.passId, user_info: mergeUserInfo(row.info, form) } },
    {
      table: CERTIFICATE_TABLE,
      data: {
        pass_id: row.passId,
        event_id: eventId,
        event_activity_id: row.activityId,
        event_activity_certificate_template_id: row.choice.id,
        ...(row.userId != null ? { user_id: row.userId } : {}),
        finish_time: form.finishTime.trim() || null,
        position: position ? Number(position) : null,
      },
    },
  ];
}

/**
 * Regenerate an issued certificate, as one all-or-nothing batch: the pass's
 * corrected details, and that certificate row's finish time and position. The
 * row keeps its id and certificate_no; saving it fires the record.updated
 * hook, which emails the participant the updated certificate.
 */
export function buildRegenerateBatch(row, certificate, form) {
  const position = String(form.position).trim();
  return [
    { table: PASS_TABLE, data: { id: row.passId, user_info: mergeUserInfo(row.info, form) } },
    {
      table: CERTIFICATE_TABLE,
      data: {
        id: certificate.id,
        finish_time: form.finishTime.trim() || null,
        position: position ? Number(position) : null,
      },
    },
  ];
}
